import { User, roleLabel } from "@/services/user-service";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, CheckCircle, XCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPhone } from "@/utils/formatters";

interface UsersTableProps {
  users: User[];
  loading?: boolean;
  showCompany?: boolean;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onActivate: (user: User) => void;
  onInactivate: (user: User) => void;
  /** Retorna se o usuário logado tem RBAC para manipular o usuário-alvo */
  canManageTarget?: (user: User) => boolean;
}

export function UsersTable({
  users,
  loading = false,
  showCompany = false,
  onEdit,
  onDelete,
  onActivate,
  onInactivate,
  canManageTarget,
}: UsersTableProps) {
  const isAllowed = (user: User) =>
    canManageTarget ? canManageTarget(user) : true;

  const columns = [
    <TableHead key="name" className="th">Nome</TableHead>,
    <TableHead key="email" className="th">E-mail</TableHead>,
    <TableHead key="cellphone" className="th">Telefone</TableHead>,
    showCompany && <TableHead key="company" className="th">Empresa</TableHead>,
    <TableHead key="role" className="th">Função</TableHead>,
    <TableHead key="status" className="th">Status</TableHead>,
    <TableHead key="actions" className="th text-center">Ações</TableHead>,
  ];

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              {columns}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell className="td">
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                  <TableCell className="td">
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                  <TableCell className="td">
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell className="td">
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell className="td">
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell className="td text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              users.map((user) => {
                const allowed = isAllowed(user);
                return (
                  <TableRow
                    key={user.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <TableCell className="td">{user.name}</TableCell>
                    <TableCell className="td">{user.email}</TableCell>
                    <TableCell className="td">
                      {formatPhone(user.cellphone)}
                    </TableCell>
                    {showCompany && (
                      <TableCell className="td">{user.company_name || "—"}</TableCell>
                    )}
                    <TableCell className="td">{roleLabel(user.role)}</TableCell>
                    <TableCell className="td">
                      <Badge
                        variant={user.status === 1 ? "default" : "secondary"}
                        className="text-xs py-0"
                      >
                        {user.status === 1 ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="td text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary"
                          onClick={() => onEdit(user)}
                          disabled={!allowed}
                          title={allowed ? "Editar" : "Sem permissão"}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => onDelete(user)}
                          disabled={!allowed}
                          title={allowed ? "Excluir" : "Sem permissão"}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        {user.status === 1 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 hover:bg-orange-500/10 hover:text-orange-500"
                            onClick={() => onInactivate(user)}
                            disabled={!allowed}
                            title={allowed ? "Inativar" : "Sem permissão"}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 hover:bg-green-500/10 hover:text-green-500"
                            onClick={() => onActivate(user)}
                            disabled={!allowed}
                            title={allowed ? "Ativar" : "Sem permissão"}
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhum usuário encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
